import { ChevronDown, Filter, RotateCcw, SlidersHorizontal } from 'lucide-react-native';
import { useEffect } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { DatePickerInput } from '../../../components/primitives/DatePickerInput';
import { FormInput } from '../../../components/primitives/FormInput';
import { Select } from '../../../components/primitives/Select';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { VoiceCallOutcome, VoiceCallStatus } from '../types';

const STATUS_OPTIONS: Array<{ labelKey: string; value: VoiceCallStatus | '' }> = [
  { labelKey: 'filterAll', value: '' },
  { labelKey: 'filterScheduled', value: 'scheduled' },
  { labelKey: 'filterInProgress', value: 'dialing' },
  { labelKey: 'filterCompleted', value: 'completed' },
  { labelKey: 'filterNoAnswer', value: 'no_answer' },
  { labelKey: 'filterError', value: 'technical_failure' },
];

interface FilterOption {
  value: string;
  label: string;
}

interface CallFiltersProps {
  expanded: boolean;
  onToggle: () => void;
  activeCount: number;
  onClear: () => void;
  status: VoiceCallStatus | '';
  onStatusChange: (status: VoiceCallStatus | '') => void;
  fromDate: string;
  onFromDateChange: (value: string) => void;
  toDate: string;
  onToDateChange: (value: string) => void;
  country: string;
  onCountryChange: (value: string) => void;
  agent: string;
  onAgentChange: (value: string) => void;
  lead: string;
  onLeadChange: (value: string) => void;
  outcome: VoiceCallOutcome | '';
  onOutcomeChange: (value: VoiceCallOutcome | '') => void;
  agentOptions: FilterOption[];
  leadOptions: FilterOption[];
  outcomeOptions: FilterOption[];
}

export function CallFilters({
  expanded,
  onToggle,
  activeCount,
  onClear,
  status,
  onStatusChange,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  country,
  onCountryChange,
  agent,
  onAgentChange,
  lead,
  onLeadChange,
  outcome,
  onOutcomeChange,
  agentOptions,
  leadOptions,
  outcomeOptions,
}: Readonly<CallFiltersProps>) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const disclosureProgress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    if (reducedMotion) {
      disclosureProgress.value = expanded ? 1 : 0;
      return;
    }

    disclosureProgress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 308 : 238,
      easing: Easing.out(Easing.cubic),
    });
  }, [disclosureProgress, expanded, reducedMotion]);

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${disclosureProgress.value * 180}deg` }],
  }));

  const panelEntering = reducedMotion
    ? undefined
    : FadeIn.duration(196).easing(Easing.out(Easing.cubic));
  const panelExiting = reducedMotion
    ? undefined
    : FadeOut.duration(140).easing(Easing.out(Easing.cubic));

  return (
    <View style={styles.wrapper}>
      <VStack spacing="sm">
        <HStack justify="between" align="center" spacing="sm" wrap>
          <HStack spacing="xs" align="center">
            <SlidersHorizontal size={15} color={colors.text.secondary} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.secondary} style={styles.title}>
              {t('voiceAgents.callsList.filtersTitle')}
            </Text>
            {activeCount > 0 ? (
              <View style={styles.countBadge}>
                <Text variant="caption" color={colors.primary.neon} style={styles.countText}>
                  {activeCount}
                </Text>
              </View>
            ) : null}
          </HStack>
          <HStack spacing="xs" align="center">
            {activeCount > 0 ? (
              <Button
                testID="calls-clear-filters"
                variant="ghost"
                size="sm"
                onPress={onClear}
                accessibilityLabel={t('voiceAgents.callsList.clearFilters')}
              >
                <HStack spacing="xs" align="center">
                  <RotateCcw size={14} color={colors.text.secondary} strokeWidth={1.8} />
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.clearFilters')}
                  </Text>
                </HStack>
              </Button>
            ) : null}
            <Button
              testID="calls-filter-toggle"
              variant={expanded ? 'secondary' : 'outline'}
              size="sm"
              onPress={onToggle}
              accessibilityLabel={
                expanded
                  ? t('voiceAgents.callsList.hideFilters')
                  : t('voiceAgents.callsList.showFilters')
              }
              accessibilityState={{ expanded }}
              aria-expanded={expanded}
              aria-controls="calls-advanced-filters"
            >
              <HStack spacing="xs" align="center">
                <Filter size={14} color={colors.text.secondary} strokeWidth={1.8} />
                <Text variant="caption" color={colors.text.primary}>
                  {expanded
                    ? t('voiceAgents.callsList.hideFilters')
                    : t('voiceAgents.callsList.showFilters')}
                </Text>
                <Animated.View testID="calls-filter-chevron" style={chevronStyle}>
                  <ChevronDown size={14} color={colors.text.secondary} strokeWidth={1.8} />
                </Animated.View>
              </HStack>
            </Button>
          </HStack>
        </HStack>

        <View
          accessibilityRole="radiogroup"
          accessibilityLabel={t('voiceAgents.callsList.statusFiltersA11y')}
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.statusRow}
          >
            {STATUS_OPTIONS.map((option) => {
              const active = status === option.value;
              const label = t(`voiceAgents.callsList.${option.labelKey}`);
              return (
                <Button
                  key={option.value || 'all'}
                  variant={active ? 'primary' : 'outline'}
                  size="sm"
                  onPress={() => onStatusChange(option.value)}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: active, selected: active }}
                  accessibilityLabel={`${t('voiceAgents.callsList.filterByPrefix')} ${label}`}
                >
                  {label}
                </Button>
              );
            })}
          </ScrollView>
        </View>

        {expanded ? (
          <Animated.View
            testID="calls-advanced-filters-motion"
            accessibilityLabel={t('voiceAgents.callsList.advancedFiltersA11y')}
            entering={panelEntering}
            exiting={panelExiting}
          >
            <View
              nativeID="calls-advanced-filters"
              testID="calls-advanced-filters"
              style={styles.panel}
            >
              <View style={styles.fields}>
                <VStack spacing="xs" style={styles.field}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.fromLabel')}
                  </Text>
                  <DatePickerInput
                    value={fromDate}
                    onChange={onFromDateChange}
                    placeholder={t('voiceAgents.callsList.fromPlaceholder')}
                    testID="calls-from-date"
                  />
                </VStack>
                <VStack spacing="xs" style={styles.field}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.toLabel')}
                  </Text>
                  <DatePickerInput
                    value={toDate}
                    onChange={onToDateChange}
                    placeholder={t('voiceAgents.callsList.toPlaceholder')}
                    testID="calls-to-date"
                  />
                </VStack>
                <VStack spacing="xs" style={styles.countryField}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.countryLabel')}
                  </Text>
                  <FormInput
                    value={country}
                    onChangeText={onCountryChange}
                    placeholder={t('voiceAgents.callsList.countryPlaceholder')}
                    accessibilityLabel={t('voiceAgents.callsList.countryA11y')}
                    autoCapitalize="characters"
                    maxLength={2}
                    style={styles.countryInput}
                  />
                </VStack>
                <VStack spacing="xs" style={styles.field}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.filterAgentLabel')}
                  </Text>
                  <Select
                    variant="dropdown"
                    options={agentOptions}
                    value={agent}
                    onChange={onAgentChange}
                    placeholder={t('voiceAgents.callsList.filterAgentPlaceholder')}
                    testID="filter-agent-select"
                    searchable
                    searchPlaceholder={t('common.selectSearchPlaceholder')}
                    emptySearchLabel={t('common.selectNoMatches')}
                  />
                </VStack>
                <VStack spacing="xs" style={styles.field}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.filterLeadLabel')}
                  </Text>
                  <Select
                    variant="dropdown"
                    options={leadOptions}
                    value={lead}
                    onChange={onLeadChange}
                    placeholder={t('voiceAgents.callsList.filterLeadPlaceholder')}
                    testID="filter-lead-select"
                    searchable
                    searchPlaceholder={t('common.selectSearchPlaceholder')}
                    emptySearchLabel={t('common.selectNoMatches')}
                  />
                </VStack>
                <VStack spacing="xs" style={styles.field}>
                  <Text variant="caption" color={colors.text.secondary}>
                    {t('voiceAgents.callsList.filterOutcomeLabel')}
                  </Text>
                  <Select
                    variant="dropdown"
                    options={outcomeOptions}
                    value={outcome}
                    onChange={(value) => onOutcomeChange(value as VoiceCallOutcome | '')}
                    placeholder={t('voiceAgents.callsList.filterOutcomePlaceholder')}
                    testID="filter-outcome-select"
                  />
                </VStack>
              </View>
            </View>
          </Animated.View>
        ) : null}
      </VStack>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexShrink: 0,
  },
  title: {
    fontWeight: tokens.fontWeight.semibold,
  },
  countBadge: {
    minWidth: 20,
    height: 20,
    paddingHorizontal: tokens.spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary.muted,
  },
  countText: {
    fontWeight: tokens.fontWeight.bold,
    fontVariant: ['tabular-nums'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xxs,
  },
  panel: {
    padding: tokens.spacing.md,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
  },
  fields: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.md,
  },
  field: {
    flex: 1,
    flexBasis: 220,
    minWidth: 180,
  },
  countryField: {
    flexBasis: 130,
    minWidth: 120,
    maxWidth: 180,
  },
  countryInput: {
    minHeight: 38,
    backgroundColor: colors.background.input,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    color: colors.text.primary,
    fontSize: tokens.fontSize.smPlus,
  },
});
